import { Controller } from "@hotwired/stimulus"

// Manages adding products to a routine log (morning or evening routine)
// Listens for product:selected events from product_search_controller
export default class extends Controller {
  static targets = ["product", "container", "addButton"]
  static values = {
    routineId: Number,
    timeOfDay: String
  }

  connect() {
    this.boundAddProduct = this.addProduct.bind(this)
    window.addEventListener('product:selected', this.boundAddProduct)
  }

  disconnect() {
    window.removeEventListener('product:selected', this.boundAddProduct)
  }

  addProduct(event) {
    const { routineId, timeOfDay, productId, productName, productThumbnailUrl } = event.detail

    // Only handle if this event is for our routine and time of day
    if (routineId != this.routineIdValue || timeOfDay !== this.timeOfDayValue) {
      return
    }

    // Check if this product already exists in the form
    const existingProduct = this.productTargets.find(productDiv => {
      const checkbox = productDiv.querySelector('input[type="checkbox"]')
      return checkbox && checkbox.value === productId.toString()
    })

    if (existingProduct) {
      // Product already exists - just check it instead of adding a duplicate
      const checkbox = existingProduct.querySelector('input[type="checkbox"]')
      if (checkbox && !checkbox.checked) {
        checkbox.checked = true
        checkbox.dispatchEvent(new Event('change'))
      }
      return
    }

    // Get the last product element to clone
    const productTargets = this.productTargets
    if (productTargets.length === 0) {
      return
    }

    const lastProduct = productTargets[productTargets.length - 1]
    const clonedProduct = lastProduct.cloneNode(true)

    // Update the cloned element with new product data
    this.updateClonedProduct(clonedProduct, productId, productName, productThumbnailUrl)

    // Insert before the "Add" button
    this.containerTarget.insertBefore(clonedProduct, this.addButtonTarget)
  }

  updateClonedProduct(clonedDiv, productId, productName, productThumbnailUrl) {
    // Set x-data to checked: true
    clonedDiv.setAttribute('x-data', '{ checked: true, createdAt: null }')

    // Update checkbox
    const checkbox = clonedDiv.querySelector('input[type="checkbox"]')
    if (checkbox) {
      checkbox.value = productId
      checkbox.id = `${productId}_${this.routineIdValue}_${this.timeOfDayValue}`
      checkbox.checked = true
    }

    // Update label
    const label = clonedDiv.querySelector('label')
    if (label && checkbox) {
      label.htmlFor = checkbox.id

      // Update category text to "Custom Step"
      const categorySpan = label.querySelector('span')
      if (categorySpan) {
        categorySpan.textContent = 'Custom Step'
      }
    }

    // Update image
    const img = clonedDiv.querySelector('img')
    if (img) {
      img.src = productThumbnailUrl
      img.alt = productName
    }

    // Remove hidden id field (it's a new record)
    const hiddenInputs = clonedDiv.querySelectorAll('input[type="hidden"]')
    hiddenInputs.forEach(input => {
      if (input.name.endsWith('[id]')) {
        input.remove()
      }
    })

    // Update all input names and IDs to use a new index
    this.updateInputIndices(clonedDiv)

    // Set _destroy to "0"
    const destroyInput = clonedDiv.querySelector('input[name*="_destroy"]')
    if (destroyInput) {
      destroyInput.value = "0"
    }
  }

  updateInputIndices(clonedDiv) {
    const allInputs = clonedDiv.querySelectorAll('input')

    allInputs.forEach(input => {
      // Update name attribute: routine_log_products_attributes][0] -> routine_log_products_attributes][1000]
      const nameMatch = input.name.match(/routine_log_products_attributes\]\[(\d+)\]/)
      if (nameMatch) {
        const number = parseInt(nameMatch[1], 10)
        input.name = input.name.replace(nameMatch[0], `routine_log_products_attributes][${number + 1000}]`)
      }

      // Update id attribute: routine_log_products_attributes_0_ -> routine_log_products_attributes_1000_
      if (input.id.includes('routine_log_products_attributes')) {
        const idMatch = input.id.match(/routine_log_products_attributes_(\d+)_/)
        if (idMatch) {
          const number = parseInt(idMatch[1], 10)
          input.id = input.id.replace(idMatch[0], `routine_log_products_attributes_${number + 1000}_`)
        }
      }
    })
  }
}
